import { useState } from "react";
import { Users as UsersIcon } from "lucide-react";
import type { ClientRole, UserProfile } from "@school-sales-ai/shared";

/**
 * A school's roster holds admins and members only — `userRepository.list`
 * excludes super_admins, and account managers carry no clientId — so a wider
 * role than `ClientRole` can never appear in these dialogs. The server enforces
 * the same set: `/crm/users` rejects a platform role at the Zod layer.
 */
function asClientRole(role: UserProfile["role"]): ClientRole {
  return role === "admin" ? "admin" : "member";
}
import { useAuthStore } from "@/stores/auth";
import { Avatar } from "@/components/Avatar";
import { RoleBadge } from "@/components/RoleBadge";
import { StatusBadge } from "@/components/StatusBadge";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useUsers,
  useInviteUser,
  useUpdateUser,
  useDisableUser,
  useEnableUser,
  useResetPassword,
  useResendInvite,
} from "@/hooks/useUsers";

// ─── Types ───────────────────────────────────────────────────────────────────

// Desktop-only modals (Radix Dialog, portals to body)
type ModalState =
  | { type: "invite"; success?: true }
  | { type: "edit"; user: UserProfile }
  | { type: "disable"; user: UserProfile }
  | { type: "resetPassword"; user: UserProfile; success?: true }
  | { type: "resendInvite"; user: UserProfile; success?: true }
  | null;

// Mobile-only sheets (absolute positioned within the mobile section)
type SheetState =
  | { type: "invite"; success?: true }
  | { type: "edit"; user: UserProfile }
  | null;

// ─── Shared sub-components ───────────────────────────────────────────────────

function UserAvatar({ name }: { name: string }): React.JSX.Element {
  return (
    <span className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-ink-100 text-xs font-medium text-ink-700">
      {name[0]?.toUpperCase() ?? "?"}
    </span>
  );
}

function SkeletonRows(): React.JSX.Element {
  return (
    <>
      {[1, 2, 3].map((i) => (
        <TableRow key={i}>
          {[1, 2, 3, 4, 5].map((j) => (
            <TableCell key={j}>
              <div className="h-4 animate-pulse rounded bg-ink-100" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function EmptyState({
  onInvite,
}: {
  onInvite: () => void;
}): React.JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-20">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-ink-100">
        <UsersIcon size={22} className="text-ink-400" />
      </div>
      <p className="font-display text-lg font-bold text-ink-700">
        Aún no hay usuarios
      </p>
      <p className="text-xs text-ink-400">
        Invita a tu primer miembro del equipo para empezar.
      </p>
      <Button size="sm" onClick={onInvite} className="mt-1">
        Invitar primer miembro
      </Button>
    </div>
  );
}

function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-ink-700">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-ink-500">{hint}</p>}
    </div>
  );
}

// ─── Desktop modals ───────────────────────────────────────────────────────────

function InviteModal({
  modal,
  onClose,
  onSuccess,
}: {
  modal: { type: "invite"; success?: true };
  onClose: () => void;
  onSuccess: (email: string) => void;
}): React.JSX.Element {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<ClientRole>("member");
  const inviteUser = useInviteUser();

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    inviteUser.mutate({ name, email, role }, { onSuccess: () => onSuccess(email) });
  }

  const inputClass =
    "w-full rounded-md border border-ink-100 px-3 py-2 text-xs text-ink-900 outline-none transition focus:border-moss";

  if (modal.success) {
    return (
      <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent className="max-w-[420px]">
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <span className="text-3xl">✉️</span>
            <p className="font-display text-xl font-bold text-ink-900">
              Invitación enviada
            </p>
            <p className="text-xs text-ink-500">Se mandó un correo a {email}.</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold text-ink-900">
            Invitar usuario
          </DialogTitle>
          <DialogDescription className="text-xs text-ink-500">
            La persona recibirá un correo para crear su contraseña.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <Field label="Nombre completo">
            <input
              type="text"
              placeholder="Ej. Ana García"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className={inputClass}
            />
          </Field>
          <Field
            label="Correo electrónico"
            hint="Recibirá un correo para establecer su contraseña."
          >
            <input
              type="email"
              placeholder="nombre@viadiseno.mx"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={inputClass}
            />
          </Field>
          <Field label="Rol">
            <Select value={role} onValueChange={(v) => setRole(v as ClientRole)}>
              <SelectTrigger className="text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Miembro</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <div className="flex justify-end gap-2 border-t border-ink-100 pt-4">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={!name.trim() || !email.trim() || inviteUser.isPending}
            >
              {inviteUser.isPending ? "Enviando…" : "Enviar invitación"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditModal({
  user,
  onClose,
}: {
  user: UserProfile;
  onClose: () => void;
}): React.JSX.Element {
  const [name, setName] = useState(user.name);
  const [role, setRole] = useState<ClientRole>(asClientRole(user.role));
  const updateUser = useUpdateUser();

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    updateUser.mutate({ id: user.id, name, role }, { onSuccess: onClose });
  }

  const inputClass =
    "w-full rounded-md border border-ink-100 px-3 py-2 text-xs text-ink-900 outline-none transition focus:border-moss";

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold text-ink-900">
            Editar usuario
          </DialogTitle>
          <DialogDescription className="text-xs text-ink-500">
            Actualiza el nombre o el rol del usuario.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <Field label="Nombre completo">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className={inputClass}
            />
          </Field>
          <Field label="Correo electrónico">
            <div className="w-full rounded-md border border-ink-100 bg-ink-050 px-3 py-2 text-xs text-ink-500">
              {user.email}
            </div>
          </Field>
          <Field label="Rol">
            <Select value={role} onValueChange={(v) => setRole(v as ClientRole)}>
              <SelectTrigger className="text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Miembro</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <div className="flex justify-end gap-2 border-t border-ink-100 pt-4">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={!name.trim() || updateUser.isPending}
            >
              {updateUser.isPending ? "Guardando…" : "Guardar cambios"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DisableDialog({
  user,
  onClose,
}: {
  user: UserProfile;
  onClose: () => void;
}): React.JSX.Element {
  const disableUser = useDisableUser();

  function handleConfirm(): void {
    disableUser.mutate({ id: user.id, name: user.name }, { onSuccess: onClose });
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-[380px]">
        <p className="font-display text-lg font-bold text-ink-900">
          ¿Deshabilitar a {user.name}?
        </p>
        <p className="mb-5 mt-2 text-xs leading-relaxed text-ink-500">
          Esta persona perderá el acceso de inmediato y su sesión activa será
          cerrada.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <button
            onClick={handleConfirm}
            disabled={disableUser.isPending}
            className="rounded-md bg-[oklch(0.55_0.18_16)] px-3 py-1.5 text-xs font-medium text-white transition hover:bg-[oklch(0.48_0.18_16)] disabled:opacity-50"
          >
            {disableUser.isPending ? "Deshabilitando…" : "Deshabilitar"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({
  modal,
  onClose,
  onSuccess,
}: {
  modal: { type: "resetPassword"; user: UserProfile; success?: true };
  onClose: () => void;
  onSuccess: () => void;
}): React.JSX.Element {
  const { user } = modal;
  const resetPassword = useResetPassword();

  function handleConfirm(): void {
    resetPassword.mutate(
      { id: user.id, email: user.email },
      { onSuccess: onSuccess },
    );
  }

  if (modal.success) {
    return (
      <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent className="max-w-[380px]">
          <p className="font-display text-lg font-bold text-ink-900">
            Correo enviado
          </p>
          <p className="mb-5 mt-2 text-xs leading-relaxed text-ink-500">
            Se mandó un enlace para restablecer la contraseña a{" "}
            <strong className="text-ink-700">{user.email}</strong>.
          </p>
          <div className="flex justify-end">
            <Button size="sm" onClick={onClose}>
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-[380px]">
        <p className="font-display text-lg font-bold text-ink-900">
          ¿Enviar reset de contraseña a {user.name}?
        </p>
        <p className="mb-5 mt-2 text-xs leading-relaxed text-ink-500">
          Recibirá un enlace por correo para establecer una nueva contraseña.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={handleConfirm}
            disabled={resetPassword.isPending}
          >
            {resetPassword.isPending ? "Enviando…" : "Enviar correo de reset"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ResendInviteDialog({
  modal,
  onClose,
  onSuccess,
}: {
  modal: { type: "resendInvite"; user: UserProfile; success?: true };
  onClose: () => void;
  onSuccess: () => void;
}): React.JSX.Element {
  const { user } = modal;
  const resendInvite = useResendInvite();

  function handleConfirm(): void {
    resendInvite.mutate({ id: user.id, email: user.email }, { onSuccess });
  }

  if (modal.success) {
    return (
      <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent className="max-w-[380px]">
          <p className="font-display text-lg font-bold text-ink-900">Invitación enviada</p>
          <p className="mb-5 mt-2 text-xs leading-relaxed text-ink-500">
            Se reenvió el correo de invitación a{" "}
            <strong className="text-ink-700">{user.email}</strong>.
          </p>
          <div className="flex justify-end">
            <Button size="sm" onClick={onClose}>Cerrar</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-[380px]">
        <p className="font-display text-lg font-bold text-ink-900">
          ¿Reenviar invitación a {user.name}?
        </p>
        <p className="mb-5 mt-2 text-xs leading-relaxed text-ink-500">
          Recibirá un nuevo correo para crear su contraseña. El enlace anterior quedará inválido.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancelar</Button>
          <Button size="sm" onClick={handleConfirm} disabled={resendInvite.isPending}>
            {resendInvite.isPending ? "Enviando…" : "Reenviar invitación"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Mobile components ────────────────────────────────────────────────────────

type MobileUserCardProps = {
  user: UserProfile;
  isSelf: boolean;
  onEdit: () => void;
  onDisable: () => void;
  onEnable: () => void;
  onResendInvite: () => void;
};

function MobileUserCard({
  user,
  isSelf,
  onEdit,
  onDisable,
  onEnable,
  onResendInvite,
}: MobileUserCardProps): React.JSX.Element {
  return (
    <div
      className={`border border-ink-100 rounded-[14px] p-[14px] ${
        !user.active ? "opacity-65 bg-ink-050" : "bg-white"
      }`}
    >
      <div className="flex items-center gap-2.5">
        <Avatar name={user.name} size={40} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[14px] font-semibold text-ink-900 truncate">
              {user.name}
            </span>
            {isSelf && (
              <span className="text-[10px] text-ink-400 flex-shrink-0">Tú</span>
            )}
          </div>
          <div className="text-[11px] text-ink-500 font-mono truncate mt-[1px]">
            {user.email}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <RoleBadge role={user.role} />
          <StatusBadge active={user.active} verified={user.verified} />
        </div>
      </div>

      <div className="flex gap-2 mt-2.5 pt-2.5 border-t border-ink-050">
        <button
          type="button"
          onClick={onEdit}
          className="flex-1 py-2 bg-ink-050 border border-ink-100 rounded-[6px] text-[12px] font-medium text-ink-700 cursor-pointer"
        >
          Editar
        </button>
        {!isSelf && user.active && !user.verified && (
          <button
            type="button"
            onClick={onResendInvite}
            className="flex-1 py-2 bg-amber-50 border border-amber-200 rounded-[6px] text-[12px] font-medium text-amber-700 cursor-pointer"
          >
            Reenviar invitación
          </button>
        )}
        {!isSelf && user.active && (
          <button
            type="button"
            onClick={onDisable}
            className="flex-1 py-2 bg-[oklch(0.96_0.025_16)] rounded-[6px] text-[12px] font-medium text-[oklch(0.40_0.14_16)] cursor-pointer"
          >
            Deshabilitar
          </button>
        )}
        {!isSelf && !user.active && (
          <button
            type="button"
            onClick={onEnable}
            className="flex-1 py-2 bg-[oklch(0.94_0.04_155)] rounded-[6px] text-[12px] font-semibold text-[oklch(0.30_0.10_155)] cursor-pointer"
          >
            Habilitar
          </button>
        )}
      </div>
    </div>
  );
}

function SkeletonCard(): React.JSX.Element {
  return <div className="h-[110px] rounded-[14px] bg-ink-100 animate-pulse" />;
}

// ─── Bottom sheet wrapper ─────────────────────────────────────────────────────

function BottomSheet({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div
      className="absolute inset-0 z-50 flex flex-col justify-end bg-black/30"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-t-2xl animate-sheet-up max-h-[85%] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center pt-4 pb-2">
          <div className="w-10 h-1 rounded-full bg-ink-200" />
        </div>
        <div className="px-5 pb-8">
          <div className="font-display font-bold text-[20px] text-ink-900 mb-4">
            {title}
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

// ─── Mobile invite sheet ──────────────────────────────────────────────────────

function MobileInviteSheet({
  modal,
  onClose,
  onSuccess,
}: {
  modal: { type: "invite"; success?: true };
  onClose: () => void;
  onSuccess: (email: string) => void;
}): React.JSX.Element {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<ClientRole>("member");
  const inviteUser = useInviteUser();

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    inviteUser.mutate({ name, email, role }, { onSuccess: () => onSuccess(email) });
  }

  const inputClass =
    "w-full rounded-[10px] border border-ink-100 px-3 py-2.5 text-[13px] text-ink-900 outline-none transition focus:border-moss";

  if (modal.success) {
    return (
      <BottomSheet title="Invitar usuario" onClose={onClose}>
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <span className="text-3xl">✉️</span>
          <p className="font-display text-[18px] font-bold text-ink-900">
            Invitación enviada
          </p>
          <p className="text-[13px] text-ink-500">Se mandó un correo a {email}.</p>
        </div>
      </BottomSheet>
    );
  }

  return (
    <BottomSheet title="Invitar usuario" onClose={onClose}>
      <p className="text-[12px] text-ink-500 mb-4 -mt-2">
        La persona recibirá un correo para crear su contraseña.
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Field label="Nombre completo">
          <input
            type="text"
            placeholder="Ej. Ana García"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className={inputClass}
          />
        </Field>
        <Field
          label="Correo electrónico"
          hint="Recibirá un correo para establecer su contraseña."
        >
          <input
            type="email"
            placeholder="nombre@viadiseno.mx"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className={inputClass}
          />
        </Field>
        <Field label="Rol">
          <Select value={role} onValueChange={(v) => setRole(v as ClientRole)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="member">Miembro</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 bg-ink-050 border border-ink-100 rounded-[10px] text-[13px] font-medium text-ink-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!name.trim() || !email.trim() || inviteUser.isPending}
            className="flex-1 py-3 bg-lime rounded-[10px] text-[13px] font-semibold text-moss disabled:opacity-50"
          >
            {inviteUser.isPending ? "Enviando…" : "Enviar invitación"}
          </button>
        </div>
      </form>
    </BottomSheet>
  );
}

// ─── Mobile edit sheet ────────────────────────────────────────────────────────

function MobileEditSheet({
  user,
  onClose,
}: {
  user: UserProfile;
  onClose: () => void;
}): React.JSX.Element {
  const [name, setName] = useState(user.name);
  const [role, setRole] = useState<ClientRole>(asClientRole(user.role));
  const updateUser = useUpdateUser();

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    updateUser.mutate({ id: user.id, name, role }, { onSuccess: onClose });
  }

  const inputClass =
    "w-full rounded-[10px] border border-ink-100 px-3 py-2.5 text-[13px] text-ink-900 outline-none transition focus:border-moss";

  return (
    <BottomSheet title="Editar usuario" onClose={onClose}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Field label="Nombre completo">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className={inputClass}
          />
        </Field>
        <Field label="Correo electrónico">
          <div className="w-full rounded-[10px] border border-ink-100 bg-ink-050 px-3 py-2.5 text-[13px] text-ink-500">
            {user.email}
          </div>
        </Field>
        <Field label="Rol">
          <Select value={role} onValueChange={(v) => setRole(v as ClientRole)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="member">Miembro</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 bg-ink-050 border border-ink-100 rounded-[10px] text-[13px] font-medium text-ink-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!name.trim() || updateUser.isPending}
            className="flex-1 py-3 bg-lime rounded-[10px] text-[13px] font-semibold text-moss disabled:opacity-50"
          >
            {updateUser.isPending ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </form>
    </BottomSheet>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function Users(): React.JSX.Element {
  const sessionUserId = useAuthStore((s) => s.session?.user.id);
  const { data: users = [], isLoading } = useUsers();
  const enableUser = useEnableUser();
  const [modal, setModal] = useState<ModalState>(null);
  const [sheet, setSheet] = useState<SheetState>(null);

  const activeCount = users.filter((u) => u.active).length;

  function openInvite(): void {
    setModal({ type: "invite" });
  }

  function openMobileInvite(): void {
    setSheet({ type: "invite" });
  }

  function closeModal(): void {
    setModal(null);
  }

  function closeSheet(): void {
    setSheet(null);
  }

  return (
    <>
      {/* ── Mobile ─────────────────────────────────── */}
      <div className="md:hidden flex flex-col h-full relative">
        <PageHeader
          title="Equipo"
          subtitle={`${activeCount} activos`}
          right={
            <button
              type="button"
              onClick={openMobileInvite}
              className="flex items-center gap-1 px-2.5 py-1.5 bg-lime rounded-[8px] text-[12px] font-semibold text-moss cursor-pointer"
            >
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Invitar
            </button>
          }
        />

        <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-2 [&::-webkit-scrollbar]:w-[3px] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-ink-300">
          {isLoading && [1, 2, 3].map((i) => <SkeletonCard key={i} />)}
          {!isLoading && users.length === 0 && (
            <EmptyState onInvite={openMobileInvite} />
          )}
          {!isLoading &&
            users.map((user) => {
              const isSelf = user.id === sessionUserId;
              return (
                <MobileUserCard
                  key={user.id}
                  user={user}
                  isSelf={isSelf}
                  onEdit={() => setSheet({ type: "edit", user })}
                  onDisable={() => setModal({ type: "disable", user })}
                  onEnable={() =>
                    enableUser.mutate({ id: user.id, name: user.name })
                  }
                  onResendInvite={() => setModal({ type: "resendInvite", user })}
                />
              );
            })}
        </div>

        {/* Mobile bottom sheets — use separate sheet state to avoid conflict with desktop Dialogs */}
        {sheet?.type === "invite" && (
          <MobileInviteSheet
            modal={sheet}
            onClose={closeSheet}
            onSuccess={(email) => {
              setSheet({ type: "invite", success: true });
              setTimeout(closeSheet, 1600);
              void email;
            }}
          />
        )}
        {sheet?.type === "edit" && (
          <MobileEditSheet user={sheet.user} onClose={closeSheet} />
        )}
      </div>

      {/* ── Desktop ────────────────────────────────── */}
      <div className="hidden md:flex flex-col h-full">
        <header className="flex h-18 flex-shrink-0 items-center justify-between border-b border-ink-100 bg-surface px-6">
          <div>
            <h1 className="font-display text-[28px] font-extrabold leading-none text-ink-900">
              Usuarios
            </h1>
            <p className="mt-0.5 text-xs text-ink-500">
              {activeCount} activos · {users.length} en total
            </p>
          </div>
          <Button size="sm" onClick={openInvite}>
            + Invitar usuario
          </Button>
        </header>

        <div className="flex-1 overflow-auto p-6">
          <div className="overflow-hidden rounded-lg border border-ink-100 bg-surface shadow-sm">
            {isLoading || users.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="bg-ink-050 hover:bg-ink-050">
                    <TableHead className="text-[11px] font-semibold uppercase tracking-widest text-ink-500">
                      Usuario
                    </TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-widest text-ink-500">
                      Correo
                    </TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-widest text-ink-500">
                      Rol
                    </TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-widest text-ink-500">
                      Estado
                    </TableHead>
                    <TableHead className="text-right text-[11px] font-semibold uppercase tracking-widest text-ink-500">
                      Acciones
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <SkeletonRows />
                  ) : (
                    users.map((user) => {
                      const isSelf = user.id === sessionUserId;
                      return (
                        <TableRow
                          key={user.id}
                          className={
                            user.active
                              ? "hover:bg-[oklch(0.98_0.002_90)]"
                              : "bg-ink-050 opacity-50"
                          }
                        >
                          <TableCell>
                            <div className="flex items-center gap-2.5">
                              <UserAvatar name={user.name} />
                              <span className="text-xs font-medium text-ink-900">
                                {user.name}
                              </span>
                              {isSelf && (
                                <span className="rounded bg-ink-050 px-1.5 py-0.5 text-[10px] text-ink-500">
                                  Tú
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="font-mono text-xs text-ink-700">
                            {user.email}
                          </TableCell>
                          <TableCell>
                            <RoleBadge role={user.role} />
                          </TableCell>
                          <TableCell>
                            <StatusBadge active={user.active} verified={user.verified} />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setModal({ type: "edit", user })}
                              >
                                Editar
                              </Button>
                              {user.verified ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setModal({ type: "resetPassword", user })}
                                >
                                  Reset contraseña
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setModal({ type: "resendInvite", user })}
                                >
                                  Reenviar invitación
                                </Button>
                              )}
                              {user.active && !isSelf && (
                                <button
                                  onClick={() =>
                                    setModal({ type: "disable", user })
                                  }
                                  className="rounded border border-[oklch(0.84_0.07_16)] px-2.5 py-1 text-xs font-medium text-[oklch(0.40_0.14_16)] transition hover:bg-[oklch(0.96_0.025_16)]"
                                >
                                  Deshabilitar
                                </button>
                              )}
                              {!user.active && (
                                <button
                                  onClick={() =>
                                    enableUser.mutate({
                                      id: user.id,
                                      name: user.name,
                                    })
                                  }
                                  disabled={enableUser.isPending}
                                  className="rounded border border-[oklch(0.84_0.06_155)] px-2.5 py-1 text-xs font-medium text-[oklch(0.30_0.10_155)] transition hover:bg-[oklch(0.94_0.04_155)] disabled:opacity-50"
                                >
                                  Habilitar
                                </button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            ) : (
              <EmptyState onInvite={openInvite} />
            )}
          </div>
        </div>
      </div>

      {/* Dialogs — Radix portal, work on both desktop and mobile */}
      {modal?.type === "invite" && (
        <InviteModal
          modal={modal}
          onClose={closeModal}
          onSuccess={(email) => {
            setModal({ type: "invite", success: true });
            setTimeout(closeModal, 1600);
            void email;
          }}
        />
      )}
      {modal?.type === "edit" && (
        <EditModal user={modal.user} onClose={closeModal} />
      )}
      {modal?.type === "disable" && (
        <DisableDialog user={modal.user} onClose={closeModal} />
      )}
      {modal?.type === "resetPassword" && (
        <ResetPasswordDialog
          modal={modal}
          onClose={closeModal}
          onSuccess={() =>
            setModal({ type: "resetPassword", user: modal.user, success: true })
          }
        />
      )}
      {modal?.type === "resendInvite" && (
        <ResendInviteDialog
          modal={modal}
          onClose={closeModal}
          onSuccess={() =>
            setModal({ type: "resendInvite", user: modal.user, success: true })
          }
        />
      )}
    </>
  );
}
